import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Upload, Palette, X, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { useCompanyInfo } from "@/context/CompanyInfoContext";
import { Logo } from "@/components/Logo";
import ImageUploader from "@/components/admin/ImageUploader";

const PRESET_ACCENTS = ["#8B1E2D", "#B91C1C", "#DC2626", "#EA580C", "#D97706", "#059669", "#0369A1", "#1E40AF", "#6D28D9", "#0F172A"];

const SITE_IMAGES = [
  { key: "hero_image_url",         label: "Home — Hero Background",         kind: "hero",    hint: "Displayed behind the 'A Cooler Tomorrow' headline. Wide landscape works best." },
  { key: "home_about_image_url",   label: "Home — About Strip Background",  kind: "about",   hint: "Right-side image behind the 'A Trusted Refrigerated Trucking Partner' block." },
  { key: "about_hero_image_url",   label: "About Page — Hero",              kind: "about",   hint: "Top hero image on the /about page." },
  { key: "careers_hero_image_url", label: "Careers Page — Hero",            kind: "careers", hint: "Top hero image on the /careers page." },
  { key: "service_1_image_url",    label: "Home Service Tile 1 — Refrigerated Freight",  kind: "service", hint: "Card image for the first service tile.", aspect: "4 / 3" },
  { key: "service_2_image_url",    label: "Home Service Tile 2 — Time-Sensitive Delivery", kind: "service", hint: "Card image for the second service tile.", aspect: "4 / 3" },
  { key: "service_3_image_url",    label: "Home Service Tile 3 — Safety & Compliance",     kind: "service", hint: "Card image for the third service tile.", aspect: "4 / 3" },
  { key: "service_4_image_url",    label: "Home Service Tile 4 — Professional Fleet",      kind: "service", hint: "Card image for the fourth service tile.", aspect: "4 / 3" },
];

export default function CompanyInfo() {
  const { refresh } = useCompanyInfo();
  const [f, setF] = useState({});
  const [loading, setLoading] = useState(false);
  const [digestSending, setDigestSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { api.get("/company-info").then((r) => setF(r.data)).catch(() => {}); }, []);

  const save = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.put("/company-info", f);
      setF(data);
      await refresh();
      toast.success("Company info updated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const sendDigest = async () => {
    setDigestSending(true);
    try { await api.post("/admin/send-digest"); toast.success("Digest queued — check the notification inbox"); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setDigestSending(false); }
  };

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Logo must be under 5 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const next = { ...f, logo_url: data.url };
      setF(next);
      const saved = await api.put("/company-info", next);
      setF(saved.data);
      await refresh();
      toast.success("Logo uploaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const clearLogo = async () => {
    const next = { ...f, logo_url: "" };
    setF(next);
    try {
      const saved = await api.put("/company-info", next);
      setF(saved.data); await refresh();
      toast.success("Logo removed — wordmark restored");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="font-mono-lt text-xs uppercase tracking-[0.3em] text-[var(--lt-accent-bright)] mb-2">// Company</div>
      <h1 className="font-display font-black text-4xl text-white uppercase mb-8">Company Information</h1>

      <form onSubmit={save} className="border border-[var(--lt-border)] bg-[var(--lt-panel)] p-8 max-w-3xl space-y-5" data-testid="company-form">
        <F label="Company Name"><Input value={f.name || ""} onChange={upd("name")} /></F>
        <F label="Tagline"><Input value={f.tagline || ""} onChange={upd("tagline")} /></F>

        {/* LOGO UPLOAD */}
        <div className="border border-dashed border-[var(--lt-border)] p-6 bg-[var(--lt-bg)]" data-testid="logo-uploader">
          <Label className="text-xs font-mono-lt uppercase tracking-[0.2em] text-slate-400 mb-3 block">Company Logo</Label>
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex items-center gap-3">
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                onChange={(e) => uploadLogo(e.target.files?.[0])}
                data-testid="logo-file-input"
                className="hidden"
              />
              <Button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                data-testid="logo-upload-btn"
                className="bg-[var(--lt-accent)] hover:bg-[var(--lt-accent-hover)] text-white rounded-xl h-11 px-5 font-head uppercase tracking-widest text-xs">
                <Upload className="h-4 w-4 mr-2" /> {uploading ? "Uploading…" : (f.logo_url ? "Replace Logo" : "Upload Logo")}
              </Button>
              {f.logo_url && (
                <Button type="button" variant="outline" onClick={clearLogo}
                  data-testid="logo-clear-btn"
                  className="rounded-xl border-[var(--lt-border)] bg-transparent text-slate-300 hover:bg-[var(--lt-panel-hover)] h-11 px-4 font-head uppercase tracking-widest text-xs">
                  <X className="h-4 w-4 mr-2" /> Remove
                </Button>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-mono-lt uppercase tracking-[0.2em] text-slate-500 mb-2">Live preview</div>
              <div className="bg-black px-5 py-4 border border-[var(--lt-border)] inline-block">
                <Logo size="md" overrideUrl={f.logo_url || undefined} />
              </div>
              <div className="text-[11px] text-slate-500 mt-2">PNG, JPG, WEBP, SVG or GIF · max 5 MB · transparent PNG works best</div>
            </div>
          </div>
        </div>

        {/* ACCENT COLOR */}
        <div className="border border-dashed border-[var(--lt-border)] p-6 bg-[var(--lt-bg)]" data-testid="accent-color-picker">
          <Label className="text-xs font-mono-lt uppercase tracking-[0.2em] text-slate-400 mb-3 block flex items-center gap-2">
            <Palette className="h-3.5 w-3.5" /> Accent Color (all red highlights across the site)
          </Label>
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="color"
              value={f.accent_color || "#8B1E2D"}
              onChange={upd("accent_color")}
              data-testid="accent-color-input"
              className="h-11 w-16 rounded-xl border border-[var(--lt-border)] bg-transparent cursor-pointer"
            />
            <Input
              value={f.accent_color || "#8B1E2D"}
              onChange={upd("accent_color")}
              data-testid="accent-color-hex"
              className="bg-[var(--lt-panel)] border-[var(--lt-border)] text-white h-11 rounded-xl w-32 font-mono-lt uppercase"
            />
            <div className="flex flex-wrap gap-2">
              {PRESET_ACCENTS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setF({ ...f, accent_color: c })}
                  data-testid={`accent-preset-${c.slice(1)}`}
                  className={`h-9 w-9 border-2 transition-all ${(f.accent_color || "").toUpperCase() === c.toUpperCase() ? "border-white scale-110" : "border-transparent hover:scale-105"}`}
                  style={{ background: c }}
                  aria-label={c}
                />
              ))}
            </div>
          </div>
          <div className="text-[11px] text-slate-500 mt-3">Changes preview on save. This color replaces every red highlight (buttons, badges, links, dividers) across the entire website.</div>
        </div>

        <F label="Hero Headline"><Input value={f.hero_headline || ""} onChange={upd("hero_headline")} /></F>
        <F label="Hero Subheadline"><Textarea rows={2} value={f.hero_subheadline || ""} onChange={upd("hero_subheadline")} /></F>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <F label="Primary Phone"><Input value={f.phone || ""} onChange={upd("phone")} data-testid="c-phone" /></F>
          <F label="Primary Phone Label"><Input value={f.phone_label || ""} onChange={upd("phone_label")} placeholder="e.g. Dispatch" data-testid="c-phone-label" /></F>
          <F label="Secondary Phone"><Input value={f.phone_secondary || ""} onChange={upd("phone_secondary")} data-testid="c-phone-secondary" /></F>
          <F label="Secondary Phone Label"><Input value={f.phone_secondary_label || ""} onChange={upd("phone_secondary_label")} placeholder="e.g. After Hours" data-testid="c-phone-secondary-label" /></F>
          <F label="Primary Email (shown on site)"><Input value={f.email || ""} onChange={upd("email")} data-testid="c-email" /></F>
          <F label="Primary Email Label"><Input value={f.email_label || ""} onChange={upd("email_label")} placeholder="e.g. General, Dispatch" data-testid="c-email-label" /></F>
          <F label="Secondary Email (shown on site)"><Input value={f.email_secondary || ""} onChange={upd("email_secondary")} data-testid="c-email-secondary" /></F>
          <F label="Secondary Email Label"><Input value={f.email_secondary_label || ""} onChange={upd("email_secondary_label")} placeholder="e.g. Dispatch, Recruiting" data-testid="c-email-secondary-label" /></F>
          <F label="Notification Email (form submissions forwarded here)"><Input value={f.notification_email || ""} onChange={upd("notification_email")} placeholder="quotes-and-applications@yourcompany.com" data-testid="c-notification-email" /></F>
          <F label="Notification Phone — SMS (E.164, requires Twilio)"><Input value={f.notification_phone || ""} onChange={upd("notification_phone")} placeholder="+12245550100" data-testid="c-notification-phone" /></F>
          <F label="Slack Incoming Webhook URL (optional)" full><Input value={f.notification_slack_webhook || ""} onChange={upd("notification_slack_webhook")} placeholder="https://hooks.slack.com/services/T000/B000/xxxx" data-testid="c-notification-slack" /></F>
          <F label="CDL Class options for Driver Application (comma-separated)" full>
            <Input
              value={(f.cdl_class_options || []).join(", ")}
              onChange={(e) => setF({ ...f, cdl_class_options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
              placeholder="A, B, A+Hazmat"
              data-testid="c-cdl-options"
            />
          </F>
          <F label="Freight Quote equipment options (comma-separated)" full>
            <Input
              value={(f.freight_equipment_options || []).join(", ")}
              onChange={(e) => setF({ ...f, freight_equipment_options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
              placeholder="Dry Van, Refrigerated (Reefer), Flatbed, Power Only, Other"
              data-testid="c-equipment-options"
            />
          </F>
          <F label="Address"><Input value={f.address_line || ""} onChange={upd("address_line")} data-testid="c-address" /></F>
          <F label="Hours"><Input value={f.hours || ""} onChange={upd("hours")} data-testid="c-hours" /></F>
          <F label="MC Number"><Input value={f.mc_number || ""} onChange={upd("mc_number")} /></F>
          <F label="DOT Number"><Input value={f.dot_number || ""} onChange={upd("dot_number")} /></F>
        </div>
        <F label="About Story"><Textarea rows={5} value={f.about_story || ""} onChange={upd("about_story")} /></F>

        {/* ---------- MARKETING PHOTOGRAPHY ---------- */}
        <div className="border-t border-[var(--lt-border)] pt-6 mt-2" data-testid="site-images-section">
          <div className="flex items-center gap-2 mb-1">
            <ImageIcon className="h-4 w-4 text-[var(--lt-accent-bright)]" />
            <Label className="text-xs font-mono-lt uppercase tracking-[0.2em] text-slate-400">Site Images</Label>
          </div>
          <div className="text-[12px] text-slate-500 mb-4">
            Upload replacements for every hero and section image across the website. Leave any slot empty to fall back to the default photo.
          </div>
          <div className="grid grid-cols-1 gap-4">
            {SITE_IMAGES.map((img) => (
              <ImageUploader
                key={img.key}
                label={img.label}
                value={f[img.key] || ""}
                onChange={(url) => setF({ ...f, [img.key]: url })}
                kind={img.kind}
                testid={`img-${img.key}`}
                help={img.hint}
                aspect={img.aspect || "16 / 9"}
              />
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-2">
          <Button type="submit" disabled={loading} data-testid="save-company" className="bg-[var(--lt-accent)] hover:bg-[var(--lt-accent-hover)] text-white rounded-xl h-12 px-8 font-head uppercase tracking-widest text-sm">
            {loading ? "Saving…" : "Save"}
          </Button>
          <Button type="button" onClick={sendDigest} disabled={digestSending} variant="outline" data-testid="send-digest-btn" className="rounded-xl border-[var(--lt-border)] bg-transparent text-slate-200 hover:bg-[var(--lt-panel-hover)] h-12 px-6 font-head uppercase tracking-widest text-xs">
            <Send className="h-4 w-4 mr-2" /> {digestSending ? "Queuing…" : "Send Weekly Digest Now"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-mono-lt uppercase tracking-[0.2em] text-slate-400 mb-2 block">{label}</Label>
      <div className="[&_input]:bg-[var(--lt-bg)] [&_input]:border-[var(--lt-border)] [&_input]:text-white [&_input]:h-11 [&_input]:rounded-xl [&_textarea]:bg-[var(--lt-bg)] [&_textarea]:border-[var(--lt-border)] [&_textarea]:text-white [&_textarea]:rounded-xl">{children}</div>
    </div>
  );
}
